import { whereFilterType, WhereFilterOperators } from "src/types/types";

/**
 * Industry-standard URL parameter utilities for filtering
 *
 * Converts between:
 * - URL params: ?status=active&type=document,pdf&price_min=10&price_max=100
 * - Internal filter format: whereFilterType[]
 */

// Applied filter with display information
export interface AppliedFilter {
  id: string;
  fieldName: string;
  operator: WhereFilterOperators;
  value: string | string[];
  displayLabel: string;
  displayValue: string;
}

// Range filter value
export interface RangeValue {
  min?: number;
  max?: number;
}

// Date range filter value
export interface DateRangeValue {
  after?: string; // ISO date string
  before?: string; // ISO date string
}

/**
 * Generates a unique ID for a filter
 */
export function generateFilterId(): string {
  return `filter_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
}

/**
 * Parses a comma-separated value into an array
 * "active,pending" -> ["active", "pending"]
 */
export function parseMultiValue(value: string): string[] {
  if (!value) return [];
  return value
    .split(",")
    .map((v) => v.trim())
    .filter(Boolean);
}

/**
 * Joins an array into a comma-separated string
 * ["active", "pending"] -> "active,pending"
 */
export function joinMultiValue(values: string[]): string {
  return values.filter(Boolean).join(",");
}

/**
 * Converts URL search params to AppliedFilter[]
 *
 * Supports:
 * - Simple: ?status=active -> { fieldName: "status", value: "active" }
 * - Multi: ?status=active,pending -> { fieldName: "status", value: ["active", "pending"] }
 * - Range: ?price_min=10&price_max=100 -> two filters with ge/le operators
 * - Date: ?created_after=2024-01-01&created_before=2024-12-31
 */
export function urlParamsToFilters(
  params: URLSearchParams,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  _filterableFields: string[],
): AppliedFilter[] {
  const filters: AppliedFilter[] = [];
  const processedFields = new Set<string>();

  // Reserved params that are not filters
  const reservedParams = new Set([
    "page",
    "limit",
    "sort",
    "q",
    "search_text", // Semantic search - handled separately
    "filt", // Legacy format
  ]);

  params.forEach((value, key) => {
    if (reservedParams.has(key) || processedFields.has(key)) return;

    // Check for range params (field_min, field_max)
    const minMatch = key.match(/^(.+)_min$/);
    const maxMatch = key.match(/^(.+)_max$/);

    // Check for date range params (field_after, field_before)
    const afterMatch = key.match(/^(.+)_after$/);
    const beforeMatch = key.match(/^(.+)_before$/);

    if (minMatch) {
      const fieldName = minMatch[1];
      processedFields.add(key);
      filters.push({
        id: generateFilterId(),
        fieldName,
        operator: "ge",
        value: value,
        displayLabel: prettifyFieldName(fieldName),
        displayValue: `≥ ${value}`,
      });
    } else if (maxMatch) {
      const fieldName = maxMatch[1];
      processedFields.add(key);
      filters.push({
        id: generateFilterId(),
        fieldName,
        operator: "le",
        value: value,
        displayLabel: prettifyFieldName(fieldName),
        displayValue: `≤ ${value}`,
      });
    } else if (afterMatch) {
      const fieldName = afterMatch[1];
      processedFields.add(key);
      filters.push({
        id: generateFilterId(),
        fieldName,
        operator: "ge",
        value: value,
        displayLabel: prettifyFieldName(fieldName),
        displayValue: `After ${formatDateForDisplay(value)}`,
      });
    } else if (beforeMatch) {
      const fieldName = beforeMatch[1];
      processedFields.add(key);
      filters.push({
        id: generateFilterId(),
        fieldName,
        operator: "le",
        value: value,
        displayLabel: prettifyFieldName(fieldName),
        displayValue: `Before ${formatDateForDisplay(value)}`,
      });
    } else {
      // Check for special prefixes
      // ~ prefix means "contains" (LIKE/ILIKE)
      // ! prefix means "not equal"
      if (value.startsWith("~")) {
        // Contains/LIKE filter - strip the ~ prefix
        const actualValue = value.slice(1);
        filters.push({
          id: generateFilterId(),
          fieldName: key,
          operator: "like",
          value: actualValue,
          displayLabel: prettifyFieldName(key),
          displayValue: `contains "${actualValue}"`,
        });
      } else if (value.startsWith("!")) {
        // Not equal filter - strip the ! prefix
        const actualValue = value.slice(1);
        filters.push({
          id: generateFilterId(),
          fieldName: key,
          operator: "ne",
          value: actualValue,
          displayLabel: prettifyFieldName(key),
          displayValue: `≠ ${actualValue}`,
        });
      } else {
        // Regular filter (simple or multi-value)
        const values = parseMultiValue(value);
        const isMulti = values.length > 1;

        filters.push({
          id: generateFilterId(),
          fieldName: key,
          operator: "eq",
          value: isMulti ? values : value,
          displayLabel: prettifyFieldName(key),
          displayValue: isMulti ? values.join(", ") : value,
        });
      }
    }
  });

  return filters;
}

/**
 * Converts AppliedFilter[] to URL search params
 */
export function filtersToUrlParams(filters: AppliedFilter[]): URLSearchParams {
  const params = new URLSearchParams();

  // Group filters by field for range consolidation
  const rangeFilters = new Map<string, { min?: string; max?: string }>();
  const dateFilters = new Map<string, { after?: string; before?: string }>();

  filters.forEach((filter) => {
    const { fieldName, operator, value } = filter;

    // Handle range operators
    if (operator === "ge" || operator === "gt") {
      // Check if it's a date field
      if (isDateValue(value as string)) {
        const existing = dateFilters.get(fieldName) || {};
        existing.after = Array.isArray(value) ? value[0] : value;
        dateFilters.set(fieldName, existing);
      } else {
        const existing = rangeFilters.get(fieldName) || {};
        existing.min = Array.isArray(value) ? value[0] : value;
        rangeFilters.set(fieldName, existing);
      }
    } else if (operator === "le" || operator === "lt") {
      if (isDateValue(value as string)) {
        const existing = dateFilters.get(fieldName) || {};
        existing.before = Array.isArray(value) ? value[0] : value;
        dateFilters.set(fieldName, existing);
      } else {
        const existing = rangeFilters.get(fieldName) || {};
        existing.max = Array.isArray(value) ? value[0] : value;
        rangeFilters.set(fieldName, existing);
      }
    } else if (operator === "eq") {
      // Simple or multi-value filter
      const paramValue = Array.isArray(value) ? joinMultiValue(value) : value;
      params.set(fieldName, paramValue);
    } else if (operator === "like") {
      // Text search on a specific field
      params.set(fieldName, `~${Array.isArray(value) ? value[0] : value}`);
    } else if (operator === "ne") {
      // Not equal - prefix with !
      const paramValue = Array.isArray(value)
        ? joinMultiValue(value.map((v) => `!${v}`))
        : `!${value}`;
      params.set(fieldName, paramValue);
    }
  });

  // Add range params
  rangeFilters.forEach((range, fieldName) => {
    if (range.min !== undefined) {
      params.set(`${fieldName}_min`, range.min);
    }
    if (range.max !== undefined) {
      params.set(`${fieldName}_max`, range.max);
    }
  });

  // Add date params
  dateFilters.forEach((range, fieldName) => {
    if (range.after !== undefined) {
      params.set(`${fieldName}_after`, range.after);
    }
    if (range.before !== undefined) {
      params.set(`${fieldName}_before`, range.before);
    }
  });

  return params;
}

/**
 * Converts AppliedFilter[] to the legacy whereFilterType[] format for API calls
 *
 * IMPORTANT: The legacy format uses different conventions:
 * - comparison_operator: "like" for contains search (NOT "eq" with ~ prefix)
 * - filter_value: plain value without ~ or ! prefixes
 */
export function appliedFiltersToWhereFilters(
  filters: AppliedFilter[],
): whereFilterType[] {
  return filters.map((filter) => {
    // Clean the value - remove any URL format prefixes that might have slipped through
    let cleanValue = Array.isArray(filter.value)
      ? filter.value.join(",")
      : String(filter.value);

    // Strip ~ or ! prefix if present (these are URL format only, not for legacy JSON)
    if (cleanValue.startsWith("~") || cleanValue.startsWith("!")) {
      cleanValue = cleanValue.slice(1);
    }

    return {
      filter_on: {
        group: "dataset",
        name: filter.fieldName,
        column: filter.fieldName,
      },
      comparison_operator: filter.operator,
      value_datatype: inferDataType(filter.value),
      filter_value: cleanValue,
    };
  });
}

/**
 * Converts legacy whereFilterType[] to AppliedFilter[]
 */
export function whereFiltersToAppliedFilters(
  filters: whereFilterType[],
): AppliedFilter[] {
  return filters.map((filter) => {
    const fieldName = filter.filter_on.name || filter.filter_on.column;
    const value = filter.filter_value;
    const isMulti = value.includes(",") && filter.comparison_operator === "eq";

    return {
      id: generateFilterId(),
      fieldName,
      operator: filter.comparison_operator,
      value: isMulti ? value.split(",") : value,
      displayLabel: prettifyFieldName(fieldName),
      displayValue: formatFilterValueForDisplay(
        filter.comparison_operator,
        value,
      ),
    };
  });
}

/**
 * Parses legacy JSON filter format from URL
 * ?filt=[{...}] -> whereFilterType[]
 */
export function parseLegacyFilterParam(filtParam: string): whereFilterType[] {
  if (!filtParam) return [];

  try {
    const parsed = JSON.parse(filtParam);
    if (Array.isArray(parsed)) {
      return parsed;
    }
    return [];
  } catch (e) {
    console.warn("Failed to parse legacy filter param:", e);
    return [];
  }
}

/**
 * Builds the full URL with all params (filters, pagination, sorting, search)
 */
export function buildDatasetViewerUrl(options: {
  basePath: string;
  filters: AppliedFilter[];
  page?: number;
  limit?: number;
  sort?: string;
  search?: string;
}): string {
  const { basePath, filters, page, limit, sort, search } = options;

  const params = filtersToUrlParams(filters);

  // Add pagination
  if (page !== undefined && page > 0) {
    params.set("page", String(page));
  }
  if (limit !== undefined) {
    params.set("limit", String(limit));
  }

  // Add sorting
  if (sort) {
    params.set("sort", sort);
  }

  // Add search
  if (search) {
    params.set("q", search);
  }

  const queryString = params.toString();
  return queryString ? `${basePath}?${queryString}` : basePath;
}

// ============================================================================
// Helper functions
// ============================================================================

/**
 * Prettifies a field name for display
 * "file_type" -> "File Type"
 * "createdAt" -> "Created At"
 */
export function prettifyFieldName(fieldName: string): string {
  // Special cases
  const specialCases: Record<string, string> = {
    file_name: "File Name",
    file_type: "File Type",
    document_type: "Document Type",
    source_uid: "Source UID",
    create_date: "Created Date",
    created_date: "Created Date",
    last_update_date: "Last Updated",
    dataset_fk: "Dataset ID",
    version_num: "Version",
  };

  const lowerName = fieldName.toLowerCase();
  if (specialCases[lowerName]) {
    return specialCases[lowerName];
  }

  // Convert snake_case or camelCase to Title Case
  return fieldName
    .replace(/_/g, " ")
    .replace(/([A-Z])/g, " $1")
    .trim()
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");
}

/**
 * Formats a filter value for display in chips
 */
function formatFilterValueForDisplay(
  operator: WhereFilterOperators,
  value: string,
): string {
  const operatorSymbols: Record<WhereFilterOperators, string> = {
    eq: "",
    ne: "≠ ",
    lt: "< ",
    gt: "> ",
    le: "≤ ",
    ge: "≥ ",
    like: "contains ",
  };

  const prefix = operatorSymbols[operator] || "";

  // Handle multi-value
  if (value.includes(",")) {
    return value.split(",").join(", ");
  }

  return `${prefix}${value}`;
}

/**
 * Formats a date string for display
 */
function formatDateForDisplay(dateStr: string): string {
  try {
    const date = new Date(dateStr);
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  } catch {
    return dateStr;
  }
}

/**
 * Checks if a value looks like a date
 */
function isDateValue(value: string): boolean {
  // ISO date format or common date patterns
  return /^\d{4}-\d{2}-\d{2}/.test(value);
}

/**
 * Infers the data type from a filter value
 */
function inferDataType(value: string | string[]): string {
  const testValue = Array.isArray(value) ? value[0] : value;

  if (!testValue) return "string";

  // Check for boolean
  if (testValue === "true" || testValue === "false") {
    return "boolean";
  }

  // Check for date
  if (isDateValue(testValue)) {
    return "date";
  }

  // Check for number
  if (!isNaN(Number(testValue)) && testValue.trim() !== "") {
    return "float";
  }

  return "string";
}

/**
 * Merges new filters with existing, replacing filters on the same field
 */
export function mergeFilters(
  existing: AppliedFilter[],
  newFilters: AppliedFilter[],
): AppliedFilter[] {
  const result = [...existing];

  newFilters.forEach((newFilter) => {
    // Find existing filter for the same field and operator
    const existingIndex = result.findIndex(
      (f) =>
        f.fieldName === newFilter.fieldName &&
        f.operator === newFilter.operator,
    );

    if (existingIndex >= 0) {
      // Replace existing
      result[existingIndex] = newFilter;
    } else {
      // Add new
      result.push(newFilter);
    }
  });

  return result;
}

/**
 * Removes a filter by ID
 */
export function removeFilterById(
  filters: AppliedFilter[],
  filterId: string,
): AppliedFilter[] {
  return filters.filter((f) => f.id !== filterId);
}

/**
 * Removes all filters for a specific field
 */
export function removeFiltersByField(
  filters: AppliedFilter[],
  fieldName: string,
): AppliedFilter[] {
  return filters.filter((f) => f.fieldName !== fieldName);
}

/**
 * Gets count of active filters for a specific field
 */
export function getActiveFilterCount(
  filters: AppliedFilter[],
  fieldName: string,
): number {
  return filters.filter((f) => f.fieldName === fieldName).length;
}

/**
 * Checks if any filters are applied
 */
export function hasActiveFilters(filters: AppliedFilter[]): boolean {
  return filters.length > 0;
}
